import { GearIcon } from "@radix-ui/react-icons";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { BlueskyIcon } from "@/components/BlueskyIcon";
import { CustomNodeSearchResult } from "@/components/CustomNodeSearchResult";
import { Description } from "@/components/Description";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import {
  AdsResult,
  PublicSearchResult,
} from "@/components/PublicSearchResultGrid";
import { Avatar } from "@/components/ui/avatar";
import { Button, ButtonAnchor, ButtonLink } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  CustomNodeHistoryItem,
  getCurrentUserQueryOptions,
  getMonetizedFeedsQueryOptions,
  getPublicFeedsQueryOptions,
  searchCustomNodesQueryOptions,
  SearchResult,
} from "@/lib/api";
import { User } from "@/lib/types";

function AdFeeds({
  adFeeds,
  visible,
  user,
  adAction,
}: {
  adFeeds: SearchResult[];
  visible: SearchResult[];
  user: User;
  adAction: React.ReactNode;
}) {
  const { data, fetchNextPage } = useInfiniteQuery(
    getMonetizedFeedsQueryOptions({
      days: 30,
      user_id: user.id.toString(),
      page_size: 20,
      initialData: {
        page: 1,
        page_size: 20,
        total_items: adFeeds.length,
        results: adFeeds,
      },
    }),
  );
  const allResults = data?.pages.flatMap((page) => page.results) || [];

  const fetchAll = async () => {
    while (true) {
      const nextPage = await fetchNextPage();

      if (nextPage.data?.pages[0]?.results.length !== 20) {
        break;
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h2 className="text-4xl md:text-5xl font-semibold p-2 instrument-serif">
          Ad Feeds
        </h2>
        {adAction}
      </div>
      <div className="flex flex-col gap-4">
        {allResults.map((result) => (
          <AdsResult key={result.algorithm.id} item={result} />
        ))}
      </div>

      {allResults.length > 0 &&
        allResults.length % 20 === 0 &&
        !visible.length && (
          <Button
            variant="ghost"
            onClick={() => {
              fetchAll().catch(console.error);
            }}
          >
            Show all
          </Button>
        )}
    </div>
  );
}

function PublicFeeds({
  publicFeeds,
  user,
}: {
  publicFeeds: SearchResult[];
  user: User;
}) {
  const { data, fetchNextPage } = useInfiniteQuery(
    getPublicFeedsQueryOptions({
      days: 30,
      user_id: user.id.toString(),
      page_size: 20,
      initialData: {
        page: 1,
        page_size: 20,
        total_items: publicFeeds.length,
        results: publicFeeds,
      },
    }),
  );
  const allResults = data?.pages.flatMap((page) => page.results) || [];

  const fetchAll = async () => {
    while (true) {
      const nextPage = await fetchNextPage();

      if (nextPage.data?.pages[0]?.results.length !== 20) {
        break;
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h2 className="text-4xl md:text-5xl font-semibold p-2 instrument-serif">
          Public Feeds
        </h2>
      </div>
      <div className="flex flex-col gap-4">
        {allResults.map((result) => (
          <PublicSearchResult key={result.algorithm.id} item={result} />
        ))}
      </div>

      {allResults.length > 0 && allResults.length % 20 === 0 && (
        <Button
          variant="ghost"
          onClick={() => {
            fetchAll().catch(console.error);
          }}
        >
          Show all
        </Button>
      )}
    </div>
  );
}

function CustomNodes({
  customNodes,
  user,
}: {
  customNodes: CustomNodeHistoryItem[];
  user: User;
}) {
  const { data, fetchNextPage } = useInfiniteQuery(
    searchCustomNodesQueryOptions({
      query: "",
      sort_by: "install_count",
      order: "desc",
      page_size: 20,
      user_id: user.id.toString(),
      initialData: {
        page: 1,
        results: customNodes,
      },
    }),
  );
  const allResults = data?.pages.flatMap((page) => page.results) || [];

  const fetchAll = async () => {
    while (true) {
      const nextPage = await fetchNextPage();

      if (nextPage.data?.pages[0]?.results.length !== 20) {
        break;
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h2 className="text-4xl md:text-5xl font-semibold p-2 instrument-serif">
          Custom Nodes
        </h2>
      </div>
      <div className="flex flex-col gap-4">
        {allResults.map((result) => (
          <CustomNodeSearchResult key={result.id} item={result} />
        ))}
      </div>

      {allResults.length > 0 && allResults.length % 20 === 0 && (
        <Button
          variant="ghost"
          onClick={() => {
            fetchAll().catch(console.error);
          }}
        >
          Show all
        </Button>
      )}
    </div>
  );
}

export function UserPage({
  adFeeds,
  publicFeeds,
  visibleIds,
  customNodes,
  user,
  accountBio,
  adAction: adActionProp,
}: {
  adFeeds?: SearchResult[];
  publicFeeds?: SearchResult[];
  visibleIds: string[];
  customNodes?: CustomNodeHistoryItem[];
  user: User;
  accountBio?: string;
  adAction?: "run-on-all" | "view-ads-profile";
}) {
  const [open, setOpen] = useState(false);
  const { data: currentUser } = useQuery(getCurrentUserQueryOptions());
  const visible =
    adFeeds && visibleIds.length > 0
      ? adFeeds.filter((i) => visibleIds.includes(`${i.algorithm.id}`))
      : adFeeds;
  const adAction =
    adActionProp === "run-on-all" ? (
      <ButtonLink
        variant="outline"
        size="sm"
        to="/app/campaigns/create"
        search={{ feeds: JSON.stringify(visibleIds) }}
      >
        Run on All
      </ButtonLink>
    ) : adActionProp === "view-ads-profile" ? (
      <ButtonLink
        variant="outline"
        size="sm"
        to="/ads/user/$userId"
        params={{ userId: user.id.toString() }}
      >
        View Ads Profile
      </ButtonLink>
    ) : null;

  return (
    <>
      <div className="pt-4 md:pt-20 md:pb-8 flex flex-col gap-4 md:gap-8 items-center">
        <Avatar
          src={user.avatar}
          alt={`${user.display_name}'s avatar`}
          className="w-16 h-16 md:w-24 md:h-24"
        />
        <h1 className="text-5xl md:text-8xl font-bold instrument-serif text-center">
          {user.display_name}
        </h1>
        <div className="flex items-center gap-2 md:gap-1 flex-1">
          <Link
            className="text-lg md:text-xl text-slate-dim"
            to="/ads/user/$userId"
            params={{ userId: user.id.toString() }}
          >
            @{user.handle}
          </Link>
          <div className="flex items-center gap-2">
            {user && currentUser && user.handle === currentUser.handle && (
              <Dialog open={open} onOpenChange={setOpen}>
                <SimpleTooltip message="Share Settings">
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <span className="sr-only">Share settings</span>
              <GearIcon />
                    </Button>
                  </DialogTrigger>
                </SimpleTooltip>

                <DialogContent>
                  <form
                    className="flex flex-col gap-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(
                        e.target as HTMLFormElement,
                      );
                      const feeds: string[] = [];

                      for (const [key, value] of formData.entries()) {
                        if (value) {
                          feeds.push(key);
                        }
                      }

                      window.location.href = `/ads/user/${
                        user.id
                      }?feeds=${encodeURIComponent(JSON.stringify(feeds))}`;
                    }}
                  >
                    <DialogHeader>
                      <DialogTitle>Share Settings</DialogTitle>
                      <DialogDescription>
                        Configure what is shared when you share this page.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                      <div className="text-sm font-medium">Feeds</div>
                      <ul className="flex flex-col gap-3">
                        {adFeeds &&
                          adFeeds.map((result) => (
                            <li key={result.algorithm.id}>
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`${result.algorithm.id}`}
                                  defaultChecked={
                                    visible &&
                                    (visible.length === 0 ||
                                      visible.some(
                                        (v) =>
                                          v.algorithm.id ===
                                          result.algorithm.id,
                                      ))
                                  }
                                  name={`${result.algorithm.id}`}
                                />
                                <label
                                  htmlFor={`${result.algorithm.id}`}
                                  className="text-sm font-medium leading-none"
                                >
                                  {result.algorithm.display_name}
                                </label>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                    <DialogFooter>
                      <Button variant="outline">Cancel</Button>
                      <Button type="submit" onClick={() => {}}>
                        Save
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
            <SimpleTooltip message="View on Bluesky">
              <ButtonAnchor
                variant="ghost"
                size="icon"
                href={`https://bsky.app/profile/${user.handle}`}
              >
                <span className="sr-only">View on Bluesky</span>
                <BlueskyIcon />
              </ButtonAnchor>
            </SimpleTooltip>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-12">
        {accountBio ? (
          <MarkdownRenderer className="text-lg leading-relaxed">
            {accountBio}
          </MarkdownRenderer>
        ) : (
          user.description && (
            <Description className="text-lg leading-relaxed">
              {user.description}
            </Description>
          )
        )}

        {visible && visible.length > 0 && (
          <AdFeeds
            adFeeds={visible}
            visible={visible}
            user={user}
            adAction={adAction}
          />
        )}
        {publicFeeds && publicFeeds.length > 0 && (
          <PublicFeeds publicFeeds={publicFeeds} user={user} />
        )}
        {customNodes && customNodes.length > 0 && (
          <CustomNodes customNodes={customNodes} user={user} />
        )}
      </div>
    </>
  );
}
